import { AdminAccountState, AdminGroup, AdminUser } from 'Interfaces/Admin';
import { canActivateUser } from 'reducers/admin/seats';

/**
 *  Guards for editing a row directly in the grid, ported from DetailsView's
 *  `updaterow`. Each returns a message key when the edit must be refused, or
 *  null when it may proceed. Deactivating a group additionally needs a
 *  confirmation, which the caller handles.
 */

export type EditRefusal = string | null;

export const checkUserEdit = (
  user: AdminUser,
  next: { active: boolean },
  account: AdminAccountState,
): EditRefusal => {
  // Nobody may switch off their own account.
  if (user.id === account.userId && user.active && !next.active) {
    return 'userActivateError';
  }
  // The account's responsible user must stay active.
  if (user.responsibleUser && user.active && !next.active) {
    return 'responsibleUserActivateError';
  }
  if (!user.active && next.active) {
    const check = canActivateUser(account, user.roles);
    if (!check.allowed) {
      return check.denied === 'office' ? 'maxOfficeUsers' : 'maxActiveUsers';
    }
  }
  return null;
};

export const checkGroupEdit = (
  group: AdminGroup,
  next: { active: boolean },
  account: AdminAccountState,
): EditRefusal => {
  // An admin may not deactivate the group they belong to.
  if (group.id === account.groupId && group.active && !next.active) {
    return 'groupActivateError';
  }
  return null;
};

/** True when the edit turns an active group off, which needs confirming first. */
export const isGroupDeactivation = (
  group: AdminGroup, next: { active: boolean },
): boolean => group.active && !next.active;

/**
 *  Rows that may never be deleted: your own account, and the responsible user.
 *  Ported from the delete handler in admin.js.
 */
export const checkUserDeletion = (
  users: AdminUser[], ids: number[], account: AdminAccountState,
): EditRefusal => {
  if (ids.some((id) => id === account.userId)) {
    return 'cannotDeleteOwnAccount';
  }
  if (ids.some((id) => users.find((u) => u.id === id)?.responsibleUser)) {
    return 'cannotDeleteResponsibleUser';
  }
  return null;
};
